import { View } from "apiClient/v2/forgeApi";
import { ActionMap } from "interfaces/models/context";
import { InspectionGroup } from "interfaces/models/inspectionGroup";
import { InspectionType, Object } from "interfaces/models/inspectionType";
import {
  PinDTO,
  PinGroupStatusType,
  TargetObject,
} from "interfaces/models/pin";
import { DisplayItem } from "utils/forge/extensions/custom-label";
import { Level } from "interfaces/models";

export enum DETAIL_MODE {
  ALL = 1,
  ADD_PIN = 2,
  DETAIL_PIN_GROUP = 3,
  ADD_PIN_EXIST = 4,
  ADD_DOCUMENT = 5,
  EDIT_DOCUMENT = 6,
}

export const OBJECT_CANNOT_SELECT_COLOR = "#ffffff";
export const TARGET_OBJECT_ADD_PIN_COLOR = "#A8C7FA";
export const TARGET_OBJECT_GROUP_DETAIL_COLOR = "#A8C7FA";
export const PIN_OBJECT_GROUP_DETAIL_COLOR = "#632DE9";
export const SELECT_PIN_OBJECT_GROUP_DETAIL_COLOR = "#009be0";
export const CURRENT_ADDED_PIN_TO_GROUP_COLOR = "#52b00a";
export const CURRENT_ADDED_PIN_TO_GROUP_BACKGROUND = "#f0fce1";
export const DEFAULT_PIN_COLOR = "#bcbcbc";
export const SELECT_PIN_OBJECT_ADD_PIN_COLOR = "#CEE741";
export const OBJECT_ACTIVE_COLOR = "#AFC501";
export const ADDED_NEW_PIN_BACKGROUND = "#eef5c4";

export const DURATION_CLEAR_HIGHLIGHT = 2000;

export type ForgeViewerData = {
  urn?: string;
  views: View[];
  guid?: string;
  height?: string | number;
  guidInspectionType?: string;
  levelData?: Level[];
};

export type HighlightObject = {
  dbId: number;
  status?: PinGroupStatusType;
} & Object;

export enum DETAIL_TAB {
  PIN_GROUP = 0,
  DOCUMENT = 1,
}

export enum ViewerAddPinType {
  BIM_OBJECT = 1,
  POSITION = 2,
}

export type InspectionDetail = {
  mode: DETAIL_MODE;
  inspectionType?: InspectionType;
  inspectionGroup?: InspectionGroup;
  inspectionGroupOfDoc?: InspectionGroup;
  forgeViewerData: ForgeViewerData;
  activeObjects: (Partial<HighlightObject> & { pinId: string })[];
  targetObjects: Object[] | undefined;
  loadingHighlightObjects: boolean;
  isDurationHighlight: boolean;
  pinsOfGroup: PinDTO[];
  pinOfGroupIds: Set<string>;
  currentInspectionPins: PinDTO[];
  currentInspectionPinIds: Set<string>;
  pinsByMode: PinDTO[];
  pinEditId: string | undefined;
  currentAddedPinIdToGroup?: string;
  clickedLabelInfo?: {
    item: DisplayItem;
  };
  detailTabIndex: number;
  message?: {
    text: string;
    color: string;
  };
  viewerAddPinType: ViewerAddPinType;
  isFilterPinNoGroup: boolean;
};

export enum Types {
  UPDATE_DETAIL_MODE = "UPDATE_DETAIL_MODE",
  UPDATE_INSPECTION_TYPE = "UPDATE_INSPECTION_TYPE",
  SET_FORGE_VIEWER = "SET_FORGE_VIEWER",
  SET_SELECTED_INSPECTION_GROUP = "SET_SELECTED_INSPECTION_GROUP",
  SET_SELECTED_GROUP_OF_DOCUMENT = "SET_SELECTED_GROUP_OF_DOCUMENT",

  SET_ACTIVE_OBJECTS = "SET_ACTIVE_OBJECTS",
  SET_TARGET_OBJECTS = "SET_TARGET_OBJECTS",
  SET_LOADING_HIGHLIGHT_OBJECTS = "SET_LOADING_HIGHLIGHT_OBJECTS",
  SET_TIMEOUT_HIGHLIGHT = "SET_TIMEOUT_HIGHLIGHT",
  SET_PIN_EDIT_ID = "SET_PIN_EDIT_ID",
  SET_CLICKED_LABEL_INFO = "SET_CLICKED_LABEL_INFO",
  SET_MESSAGE = "SET_MESSAGE",

  SET_IS_FILTER_PIN_NO_GROUP = "SET_IS_FILTER_PIN_NO_GROUP",
  CHANGE_DETAIL_TAB_INDEX = "CHANGE_DETAIL_TAB_INDEX",
  UPDATE_VIEWER_ADD_PIN_TYPE = "UPDATE_VIEWER_ADD_PIN_TYPE",
  SET_CURRENT_ADDED_PIN_ID_TO_GROUP = "SET_CURRENT_ADDED_PIN_ID_TO_GROUP",
  COMBINE_STATE = "COMBINE_STATE",
}

type InspectionDetailPayload = {
  [Types.UPDATE_DETAIL_MODE]: DETAIL_MODE;
  [Types.UPDATE_INSPECTION_TYPE]: InspectionType;
  [Types.SET_FORGE_VIEWER]: Partial<ForgeViewerData>;
  [Types.SET_SELECTED_INSPECTION_GROUP]: InspectionGroup | undefined;
  [Types.SET_SELECTED_GROUP_OF_DOCUMENT]: InspectionGroup | undefined;
  [Types.SET_ACTIVE_OBJECTS]: {
    objects: InspectionDetail["activeObjects"];
    isDurationHighlight?: boolean;
    message: InspectionDetail["message"];
  };
  [Types.SET_TARGET_OBJECTS]: TargetObject[];
  [Types.SET_LOADING_HIGHLIGHT_OBJECTS]: boolean;
  [Types.SET_PIN_EDIT_ID]: InspectionDetail["pinEditId"];
  [Types.SET_CLICKED_LABEL_INFO]: InspectionDetail["clickedLabelInfo"];
  [Types.SET_MESSAGE]: InspectionDetail["message"];
  [Types.SET_IS_FILTER_PIN_NO_GROUP]: boolean;
  [Types.SET_CURRENT_ADDED_PIN_ID_TO_GROUP]: string | undefined;
  [Types.CHANGE_DETAIL_TAB_INDEX]: number;
  [Types.UPDATE_VIEWER_ADD_PIN_TYPE]: ViewerAddPinType;
  [Types.COMBINE_STATE]: Partial<InspectionDetail>;
};

// Define the initial state
export const initialInspectionDetail: InspectionDetail = {
  mode: DETAIL_MODE.ALL,
  inspectionType: undefined,
  inspectionGroup: undefined,
  forgeViewerData: {
    urn: undefined,
    views: [],
    guid: undefined,
    height: 600,
  },
  activeObjects: [],
  targetObjects: undefined,
  currentAddedPinIdToGroup: undefined,
  loadingHighlightObjects: false,
  isDurationHighlight: false,
  // filter by groupId
  pinsOfGroup: [],
  pinOfGroupIds: new Set([]),
  // filter by search
  currentInspectionPins: [],
  currentInspectionPinIds: new Set([]),
  pinsByMode: [],
  pinEditId: undefined,
  clickedLabelInfo: undefined,
  message: undefined,
  isFilterPinNoGroup: false,
  detailTabIndex: 0,
  viewerAddPinType: ViewerAddPinType.BIM_OBJECT,
};

export type InspectionDetailActions =
  ActionMap<InspectionDetailPayload>[keyof ActionMap<InspectionDetailPayload>];

// Define the reducer function
const InspectionDetailReducer = (
  state: InspectionDetail,
  action: InspectionDetailActions
): InspectionDetail => {
  switch (action.type) {
    case Types.UPDATE_DETAIL_MODE:
      return { ...state, mode: action.payload };
    case Types.UPDATE_INSPECTION_TYPE:
      return { ...state, inspectionType: action.payload };
    case Types.SET_SELECTED_INSPECTION_GROUP:
      return { ...state, inspectionGroup: action.payload };
    case Types.SET_SELECTED_GROUP_OF_DOCUMENT:
      return { ...state, inspectionGroupOfDoc: action.payload };
    case Types.SET_FORGE_VIEWER:
      return {
        ...state,
        forgeViewerData: { ...state.forgeViewerData, ...action.payload },
      };
    case Types.SET_TARGET_OBJECTS: {
      return {
        ...state,
        targetObjects: action.payload,
      };
    }

    case Types.SET_LOADING_HIGHLIGHT_OBJECTS: {
      return {
        ...state,
        loadingHighlightObjects: action.payload,
      };
    }
    case Types.SET_ACTIVE_OBJECTS:
      return {
        ...state,
        activeObjects: action.payload.objects,
        isDurationHighlight: !!action.payload.isDurationHighlight,
        message: action.payload.message,
      };

    case Types.SET_PIN_EDIT_ID:
      return {
        ...state,
        pinEditId: action.payload,
      };
    case Types.CHANGE_DETAIL_TAB_INDEX:
      return {
        ...state,
        detailTabIndex: action.payload,
      };
    case Types.SET_CLICKED_LABEL_INFO:
      return { ...state, clickedLabelInfo: action.payload };
    case Types.SET_MESSAGE:
      return { ...state, message: action.payload };
    case Types.SET_IS_FILTER_PIN_NO_GROUP: {
      return { ...state, isFilterPinNoGroup: action.payload };
    }
    case Types.UPDATE_VIEWER_ADD_PIN_TYPE: {
      return { ...state, viewerAddPinType: action.payload };
    }
    case Types.COMBINE_STATE: {
      return { ...state, ...action.payload };
    }
    case Types.SET_CURRENT_ADDED_PIN_ID_TO_GROUP: {
      return { ...state, currentAddedPinIdToGroup: action.payload };
    }
    default:
      return state;
  }
};

export { InspectionDetailReducer };
